import { Button, Card, Checkbox, Form, Input, Space } from "antd";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { DateRangePicker as ZhDateRangePicker, DetailHeader as ZhDetailHeader } from "zhihao-ui-react";

import request from "@/api";
import { loadActivitySpecials as defaultLoadActivitySpecials } from "./ActivityPage";
import {
    ActivityRelatedProductSelector,
    loadActivityRelatedProductCandidates,
    type ActivityRelatedProductSelectorResult
} from "./ActivityRelatedProductSelector";
import type { ActivitySpecial } from "./types";
import type { ActivityFormValues, ActivityRelatedProductItem, ActivityRow } from "./types";

/** 将 Vue 文件 ID 集合转换为表单输入框可编辑的逗号文本。 */
const formatFileIdInput = (value?: Array<number | string> | string) =>
    Array.isArray(value) ? value.join(",") : value || "";

/** 将表单中的逗号文本还原为 Vue 接口需要的数字 ID 集合。 */
const parseFileIdList = (value?: Array<number | string> | string) => {
    const values = Array.isArray(value) ? value : String(value || "").split(",");
    return values
        .map(item => String(item).trim())
        .filter(Boolean)
        .map(item => Number(item))
        .filter(item => Number.isFinite(item));
};

/** 活动主图上传允许的图片 MIME 类型和单文件大小上限。 */
const ACTIVITY_MAIN_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/avif"]);
const ACTIVITY_MAIN_IMAGE_MAX_SIZE = 10 * 1024 * 1024;

/** 读取 Vue 关联商品在不同返回结构中的统一引用。 */
const getRelatedProductReference = (item: ActivityRelatedProductItem) =>
    item.id ?? item.productId ?? item.productCampaign?.code ?? item.productCampaign?.id;

/** 将 Vue ServiceSpecialVo 的嵌套用户结构归一化为表单下拉选项。 */
const normalizeActivitySpecial = (special: ActivitySpecial) => {
    const user = special.user;
    const id = special.id ?? user?.personId;
    const name = user?.name ?? special.name;
    const employeeId = user?.employeeId ?? special.employeeId;
    return {
        ...special,
        id,
        // Vue 旧版下拉框展示“姓名 | 工号”，没有工号时只展示姓名。
        name: name ? (employeeId ? `${name} | ${employeeId}` : name) : undefined
    };
};

/** 按 Vue 关联商品的 type:id 唯一键去重，兼容 id/productId 两种回显字段。 */
const dedupeRelatedProductItems = (items: ActivityRelatedProductItem[]) => {
    const seen = new Set<string>();
    const normalizedItems: ActivityRelatedProductItem[] = [];
    items.forEach(item => {
        const reference = getRelatedProductReference(item);
        const type = Number(item.type);
        if (![1, 2, 3, 4].includes(type) || reference == null) return;
        const key = `${type}:${String(reference).trim()}`;
        if (seen.has(key)) return;
        seen.add(key);
        // Vue 保存接口使用数字类型，即使编辑接口返回字符串也统一规范化。
        normalizedItems.push({ ...item, type });
    });
    return normalizedItems;
};

/** 将表单中的 type:id 文本转换为 Vue 活动关联商品结构。 */
const parseRelatedProductItems = (value?: ActivityRelatedProductItem[] | string): ActivityRelatedProductItem[] => {
    if (Array.isArray(value)) return dedupeRelatedProductItems(value);
    return dedupeRelatedProductItems(
        String(value || "")
            .split(",")
            .map(item => item.trim().split(":"))
            .filter(([type, id]) => type && id)
            .map(([type, id]) => {
                const parsedId = Number(id);
                return { type: Number(type), id: Number.isFinite(parsedId) ? parsedId : id };
            })
            .filter(item => Number.isFinite(item.type) && item.id != null && String(item.id).trim() !== "")
    );
};

export interface ActivityFormPageProps {
    loadActivity?: (id: number | string) => Promise<ActivityRow>;
    loadRelatedProducts?: (id: number | string) => Promise<ActivityRelatedProductItem[]>;
    loadRelatedProductCandidates?: (query: {
        page: number;
        size: number;
        keyword?: string;
        productTypeName?: string;
        supplierName?: string;
        type?: number;
        excludeProducts?: Array<{ productId: string; type: number }>;
    }) => Promise<ActivityRelatedProductSelectorResult>;
    loadSpecials?: (keyword: string) => Promise<ActivitySpecial[]>;
    saveActivity?: (id: number | string | undefined, values: ActivityFormValues) => Promise<void>;
    saveDraftActivity?: (values: ActivityFormValues) => Promise<void>;
}

/** 复用 Vue 活动详情接口，为编辑表单回填数据。 */
export async function loadActivity(id: number | string): Promise<ActivityRow> {
    const response = await request.service.get<ActivityRow>(`/admin/campaign/vo/${id}`);
    return response.data;
}

/** 复用 Vue 活动新增和编辑接口。 */
export async function saveActivity(id: number | string | undefined, values: ActivityFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/campaign", values);
    } else {
        await request.service.put(`/admin/campaign/${id}`, values);
    }
}

/** 复用 Vue 活动草稿保存接口。 */
export async function saveDraftActivity(values: ActivityFormValues): Promise<void> {
    await request.service.post("/admin/campaign/draft", values);
}

/** 复用 Vue 活动编辑页关联商品接口。 */
export async function loadActivityRelatedProducts(id: number | string): Promise<ActivityRelatedProductItem[]> {
    const response = await request.service.get<ActivityRelatedProductItem[]>(`/admin/campaign/products/${id}`);
    return response.data;
}

/** 商品活动新增/编辑页，覆盖 Vue 表单的核心活动信息字段。 */
export function ActivityFormPage({
    loadActivity: load = loadActivity,
    loadRelatedProducts = loadActivityRelatedProducts,
    loadRelatedProductCandidates = loadActivityRelatedProductCandidates,
    loadSpecials = defaultLoadActivitySpecials,
    saveActivity: save = saveActivity,
    saveDraftActivity: saveDraft = saveDraftActivity
}: ActivityFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const detailId = query.get("id") || undefined;
    const campaignIdParam = query.get("campaignId") || detailId;
    const campaignId = campaignIdParam && /^\d+$/.test(campaignIdParam) ? Number(campaignIdParam) : campaignIdParam;
    // 对齐 Vue campaignTitle：有活动标识时展示编辑标题，否则展示新增标题。
    const campaignTitle = campaignId ? "编辑活动" : "新增活动";
    const [form] = Form.useForm<ActivityFormValues>();
    const dateRangePickerRef = useRef<HTMLDivElement>(null);
    const validTimeStart = Form.useWatch("validTimeStart", form);
    const validTimeFinish = Form.useWatch("validTimeFinish", form);
    const selectedContentConfigs = Form.useWatch("contentConfigs", form) || [];
    const needAgreeProtocol = Form.useWatch("needAgreeProtocol", form);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [specials, setSpecials] = useState<ActivitySpecial[]>([]);
    const [loadedRelatedProductItems, setLoadedRelatedProductItems] = useState<ActivityRelatedProductItem[]>([]);
    const [activityState, setActivityState] = useState<number | undefined>();
    const pcImgMainId = Form.useWatch("pcImgMainId", form);
    const mobileImgMainId = Form.useWatch("mobileImgMainId", form);
    // Vue 仅允许新增或草稿（state=2）活动编辑专员和内容配置，已开启/已关闭活动均锁定。
    const formLocked = detailId !== undefined && activityState !== undefined && activityState !== 2;

    useEffect(() => {
        if (!detailId) {
            setActivityState(undefined);
            setLoadedRelatedProductItems([]);
            form.setFieldsValue({ title: "", contentConfigs: [1] });
            return;
        }

        void load(detailId)
            .then(result => {
                // Vue 编辑接口的枚举可能以字符串返回，加载时统一为数字供表单锁定、复选框和协议分支使用。
                const normalizedCampaign: ActivityRow["campaign"] = {
                    ...result.campaign,
                    state: result.campaign.state == null ? undefined : Number(result.campaign.state),
                    contentConfigs: result.campaign.contentConfigs?.map(config => Number(config)),
                    meetingParticipationConfigs: result.campaign.meetingParticipationConfigs?.map(config =>
                        Number(config)
                    ),
                    needAgreeProtocol:
                        result.campaign.needAgreeProtocol == null
                            ? undefined
                            : Number(result.campaign.needAgreeProtocol)
                };
                setErrorMessage(null);
                setActivityState(normalizedCampaign.state);
                // Vue 接口使用手机号数组，表单输入框只回显当前活动专员的首个号码。
                form.setFieldsValue({
                    ...(normalizedCampaign as ActivityFormValues),
                    handlerMobile: normalizedCampaign.handlerMobile?.[0] ?? "",
                    scheduleFileId:
                        normalizedCampaign.scheduleFileId == null
                            ? undefined
                            : String(normalizedCampaign.scheduleFileId),
                    meetingSupportFileIds: formatFileIdInput(normalizedCampaign.meetingSupportFileIds),
                    conferenceHotelFileIds: formatFileIdInput(normalizedCampaign.conferenceHotelFileIds),
                    pastGlories: formatFileIdInput(normalizedCampaign.pastGlories)
                });
                if (campaignId != null && normalizedCampaign.contentConfigs?.includes(2)) {
                    void loadRelatedProducts(campaignId)
                        .then(items => {
                            setLoadedRelatedProductItems(items);
                            // 保留 Vue 编辑接口的 productId/productCampaign 结构，选择器只负责可读文本展示。
                            form.setFieldsValue({ relatedProductItems: items });
                        })
                        .catch(() => setErrorMessage("活动关联商品加载失败"));
                }
            })
            .catch(() => setErrorMessage("活动详情加载失败"));
    }, [campaignId, detailId, form, load, loadRelatedProducts]);

    /** 校验并上传活动主图，成功后只回填 Vue 约定的文件 ID 字段。 */
    const uploadMainImage = async (
        field: "pcImgMainId" | "mobileImgMainId",
        label: "PC端活动主图" | "移动端活动主图",
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        // 清空 input 的值，允许用户再次选择同一个文件来重试上传。
        event.target.value = "";
        if (!file) return;
        if (!ACTIVITY_MAIN_IMAGE_TYPES.has(file.type)) {
            setErrorMessage(`${label}仅支持 JPEG、PNG、WEBP、AVIF 格式`);
            return;
        }
        if (file.size > ACTIVITY_MAIN_IMAGE_MAX_SIZE) {
            setErrorMessage(`${label}大小不能超过 10MB`);
            return;
        }

        const formData = new FormData();
        formData.append("file", file);
        setErrorMessage(null);
        try {
            // 上传服务仅在用户选择文件后加载，避免影响不涉及上传的活动详情测试和首屏加载。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            if (uploadedFile.id == null) throw new Error("uploaded file id is missing");
            form.setFieldsValue({ [field]: uploadedFile.id });
        } catch {
            setErrorMessage(`${label}上传失败`);
        }
    };

    const submit = async (draft = false) => {
        try {
            // Vue 草稿允许暂不填写正式提交字段，因此草稿保存只读取当前表单值。
            const values = draft ? form.getFieldsValue() : await form.validateFields();
            const payload = { ...values };
            if (!draft) {
                // 活动结束时间必须晚于开始时间，避免提交 Vue 接口无法接受的时间范围。
                const startTime = Date.parse(String(values.validTimeStart ?? ""));
                const finishTime = Date.parse(String(values.validTimeFinish ?? ""));
                if (Number.isFinite(startTime) && Number.isFinite(finishTime) && finishTime <= startTime) {
                    setErrorMessage("结束时间需大于开始时间");
                    return;
                }
                const currentTime = Date.now();
                // Vue 新增和已关闭活动禁止开始时间早于当前时刻，已开启活动保留历史时间兼容。
                if (Number.isFinite(startTime) && activityState !== 1 && startTime < currentTime) {
                    setErrorMessage("开始时间不能选择此刻之前的时间");
                    return;
                }
                // Vue 所有正式活动都要求结束时间晚于当前时刻。
                if (Number.isFinite(finishTime) && finishTime < currentTime) {
                    setErrorMessage("结束时间需大于当前时间");
                    return;
                }
            }
            setErrorMessage(null);
            // 沿用 Vue 表单的手机号规则，并将单个输入值转换成接口要求的数组。
            const handlerMobiles = Array.isArray(values.handlerMobile)
                ? values.handlerMobile.filter(Boolean).map(value => String(value).trim())
                : values.handlerMobile
                  ? [String(values.handlerMobile).trim()]
                  : [];
            if (!draft && !handlerMobiles.length) {
                setErrorMessage("请输入活动专员联系电话");
                return;
            }
            if (!draft && handlerMobiles.some(mobile => !/^1[3456789]\d{9}$/.test(mobile))) {
                setErrorMessage("请输入正确的手机号");
                return;
            }
            // Vue 正式活动必须同时提供 PC 和移动端主图文件 ID，草稿允许暂缺。
            if (!draft && !payload.pcImgMainId) {
                setErrorMessage("请上传PC端活动主图");
                return;
            }
            if (!draft && !payload.mobileImgMainId) {
                setErrorMessage("请上传移动端活动主图");
                return;
            }
            if (handlerMobiles.length) payload.handlerMobile = handlerMobiles;
            else delete payload.handlerMobile;
            // 空专员不提交字段，保持 Vue 新增表单的可选草稿契约。
            if (!payload.handlerId) delete payload.handlerId;
            else payload.handlerId = Number(payload.handlerId);
            if (payload.needAgreeProtocol == null || String(payload.needAgreeProtocol) === "") {
                delete payload.needAgreeProtocol;
            } else {
                payload.needAgreeProtocol = Number(payload.needAgreeProtocol);
            }
            // 协议字段只有选择同意协议时才提交，避免沿用旧草稿残留值。
            if (payload.needAgreeProtocol !== 1) {
                delete payload.protocolTitle;
                delete payload.protocolContent;
            }
            if (!payload.contactInfo) delete payload.contactInfo;
            if (payload.scheduleFileId == null || String(payload.scheduleFileId).trim() === "") {
                delete payload.scheduleFileId;
            } else {
                payload.scheduleFileId = Number(payload.scheduleFileId);
            }
            const fileIdFields = ["meetingSupportFileIds", "conferenceHotelFileIds", "pastGlories"] as const;
            fileIdFields.forEach(field => {
                const values = parseFileIdList(payload[field]);
                if (values.length) payload[field] = values;
                else delete payload[field];
            });
            const parsedRelatedProductItems = parseRelatedProductItems(payload.relatedProductItems);
            const relatedProductItems = parsedRelatedProductItems.map(item => {
                // Vue type=4 活动商品提交时需要保留 productCampaign，并补上当前活动 ID。
                if (item.type !== 4) return item;
                const source = loadedRelatedProductItems.find(
                    loadedItem =>
                        String(getRelatedProductReference(loadedItem)) === String(getRelatedProductReference(item))
                );
                if (!source?.productCampaign) return item;
                return {
                    type: 4,
                    product: {
                        ...source.productCampaign,
                        campaignId: Number.isFinite(Number(campaignId)) ? Number(campaignId) : campaignId
                    }
                };
            });
            // Vue 正式活动勾选商品配置时至少需要一条关联商品，草稿仍允许暂不添加。
            if (!draft && payload.contentConfigs?.includes(2) && !relatedProductItems.length) {
                setErrorMessage("勾选活动商品功能，请至少添加一个商品");
                return;
            }
            if (relatedProductItems.length) payload.relatedProductItems = relatedProductItems;
            else delete payload.relatedProductItems;
            // Ant Form 会为已挂载但未填写的字段生成 undefined，提交前移除以保持 Vue payload 形状。
            Object.entries(payload).forEach(([key, value]) => {
                if (value === undefined) delete payload[key as keyof typeof payload];
            });
            if (draft) await saveDraft(payload);
            else await save(campaignId, payload);
            void navigate("/productActivity/activityList");
        } catch {
            setErrorMessage("活动保存失败");
        }
    };

    // 只有用户展开/聚焦专员选择时才查询，避免表单初次进入产生额外请求。
    const loadHandlers = async () => {
        try {
            setSpecials((await loadSpecials("")).map(normalizeActivitySpecial));
        } catch {
            setErrorMessage("活动专员加载失败");
        }
    };

    return (
        <section aria-label="activity-form-page">
            <Card>
                <ZhDetailHeader title={campaignTitle} />
                {errorMessage ? <p role="alert">{errorMessage}</p> : null}
                <Form form={form} layout="vertical">
                    <Form.Item label="活动标题" name="title" rules={[{ required: true, message: "请输入活动标题" }]}>
                        <Input maxLength={100} placeholder="请输入活动标题" showCount />
                    </Form.Item>
                    <Form.Item label="活动时间">
                        <div ref={dateRangePickerRef}>
                            <ZhDateRangePicker
                                endPlaceholder="结束日期"
                                format="YYYY-MM-DD HH:mm"
                                startPlaceholder="开始日期"
                                type="datetimerange"
                                value={[
                                    validTimeStart ? String(validTimeStart) : null,
                                    validTimeFinish ? String(validTimeFinish) : null
                                ]}
                                // Vue form 使用分钟精度提交活动时间，不能把秒字段带入 campaign payload。
                                valueFormat="YYYY-MM-DD HH:mm"
                                onChange={(_value, formattedValue) => {
                                    // 以控件当前输入为准，兼容 zhihao 控件和测试替身的回调快照差异。
                                    const inputs = dateRangePickerRef.current?.querySelectorAll<HTMLInputElement>(
                                        'input[aria-label="开始时间"], input[aria-label="结束时间"]'
                                    );
                                    const currentValues =
                                        inputs && inputs.length >= 2
                                            ? [inputs[0]?.value, inputs[1]?.value]
                                            : formattedValue;
                                    form.setFieldsValue({
                                        validTimeStart: currentValues?.[0] || undefined,
                                        validTimeFinish: currentValues?.[1] || undefined
                                    });
                                }}
                            />
                            {/* 保留可编辑的 Vue 时间字段，兼容控件在不同环境下的日期输入实现。 */}
                            <input
                                aria-label="开始时间"
                                className="sr-only"
                                type="text"
                                value={validTimeStart ? String(validTimeStart) : ""}
                                onChange={event => form.setFieldsValue({ validTimeStart: event.target.value })}
                            />
                            <input
                                aria-label="结束时间"
                                className="sr-only"
                                type="text"
                                value={validTimeFinish ? String(validTimeFinish) : ""}
                                onChange={event => form.setFieldsValue({ validTimeFinish: event.target.value })}
                            />
                        </div>
                    </Form.Item>
                    {/* 保留 Vue 接口字段名，确保编辑回显值和提交 payload 仍由 Ant Form 注册。 */}
                    <Form.Item noStyle name="validTimeStart">
                        <Input type="hidden" />
                    </Form.Item>
                    <Form.Item noStyle name="validTimeFinish">
                        <Input type="hidden" />
                    </Form.Item>
                    <Form.Item label="会议地址" name="meetingAddress">
                        <Input maxLength={100} placeholder="请输入活动标题" showCount />
                    </Form.Item>
                    <Form.Item label="PC端活动主图上传">
                        <input
                            id="pc-main-image-upload"
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.avif,image/jpeg,image/png,image/webp,image/avif"
                            aria-label="PC端活动主图上传"
                            onChange={event => void uploadMainImage("pcImgMainId", "PC端活动主图", event)}
                        />
                        {pcImgMainId != null ? <p>PC端活动主图已上传：{pcImgMainId}</p> : null}
                    </Form.Item>
                    <Form.Item name="pcImgMainId" hidden>
                        <Input type="hidden" />
                    </Form.Item>
                    <Form.Item label="移动端活动主图上传">
                        <input
                            id="mobile-main-image-upload"
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.avif,image/jpeg,image/png,image/webp,image/avif"
                            aria-label="移动端活动主图上传"
                            onChange={event => void uploadMainImage("mobileImgMainId", "移动端活动主图", event)}
                        />
                        {mobileImgMainId != null ? <p>移动端活动主图已上传：{mobileImgMainId}</p> : null}
                    </Form.Item>
                    <Form.Item name="mobileImgMainId" hidden>
                        <Input type="hidden" />
                    </Form.Item>
                    <Form.Item label="活动专员联系电话" name="handlerMobile">
                        <Input placeholder="请输入活动专员联系电话" />
                    </Form.Item>
                    <Form.Item label="活动专员" name="handlerId">
                        <select aria-label="活动专员" disabled={formLocked} onFocus={() => void loadHandlers()}>
                            <option value="">请选择活动专员</option>
                            {specials.map(special => (
                                <option key={special.id} value={String(special.id)}>
                                    {special.name}
                                </option>
                            ))}
                        </select>
                    </Form.Item>
                    <Form.Item label="活动内容配置" name="contentConfigs">
                        <Checkbox.Group
                            disabled={formLocked}
                            options={[
                                { label: "活动报名", value: 1 },
                                { label: "活动商品", value: 2 }
                            ]}
                        />
                    </Form.Item>
                    {selectedContentConfigs.includes(1) ? (
                        <Card size="small" title="报名信息配置">
                            <Form.Item label="线下支付收款账户" name="offlineAccountId">
                                <Input placeholder="请输入收款账户 ID" />
                            </Form.Item>
                            <Form.Item label="收款码文件 ID" name="paymentQrCodeFileId">
                                <Input placeholder="请输入收款码文件 ID" />
                            </Form.Item>
                            <Form.Item label="参会形式配置" name="meetingParticipationConfigs">
                                <Checkbox.Group
                                    options={[
                                        { label: "冠名", value: 1 },
                                        { label: "协办", value: 2 },
                                        { label: "赞助", value: 3 },
                                        { label: "参展", value: 4 },
                                        { label: "普通参会", value: 5 }
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item
                                label="报名需同意协议"
                                name="needAgreeProtocol"
                                getValueFromEvent={event =>
                                    event.target.value === "" ? undefined : Number(event.target.value)
                                }
                            >
                                <select aria-label="报名需同意协议">
                                    <option value="">请选择</option>
                                    <option value="0">否</option>
                                    <option value="1">是</option>
                                </select>
                            </Form.Item>
                            {Number(needAgreeProtocol) === 1 ? (
                                <>
                                    <Form.Item label="协议标题" name="protocolTitle">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item label="协议内容" name="protocolContent">
                                        <Input.TextArea rows={4} />
                                    </Form.Item>
                                </>
                            ) : null}
                            <Form.Item label="活动联系方式" name="contactInfo">
                                <Input.TextArea rows={3} />
                            </Form.Item>
                            <Form.Item label="日程安排文件 ID" name="scheduleFileId">
                                <Input placeholder="请输入日程安排文件 ID" />
                            </Form.Item>
                            <Form.Item label="会议支持文件 ID" name="meetingSupportFileIds">
                                <Input placeholder="多个文件 ID 用逗号分隔" />
                            </Form.Item>
                            <Form.Item label="会议酒店文件 ID" name="conferenceHotelFileIds">
                                <Input placeholder="多个文件 ID 用逗号分隔" />
                            </Form.Item>
                            <Form.Item label="历届风采文件 ID" name="pastGlories">
                                <Input placeholder="多个文件 ID 用逗号分隔" />
                            </Form.Item>
                        </Card>
                    ) : null}
                    {selectedContentConfigs.includes(2) ? (
                        <Card size="small" title="商品信息配置">
                            <Form.Item label="关联商品配置" name="relatedProductItems">
                                <ActivityRelatedProductSelector loadCandidates={loadRelatedProductCandidates} />
                            </Form.Item>
                        </Card>
                    ) : null}
                    <Space>
                        {!formLocked ? <Button onClick={() => void submit(true)}>保存草稿</Button> : null}
                        <Button type="primary" onClick={() => void submit()}>
                            提交活动
                        </Button>
                        <Button onClick={() => void navigate("/productActivity/activityList")}>返回</Button>
                    </Space>
                </Form>
            </Card>
        </section>
    );
}

export default ActivityFormPage;
